import React, { useEffect, useRef, useState } from 'react';

/**
 * 3D contribution landscape — pure WebGL, no deps.
 * Bars for each day are laid along a large-radius curve ("globe"); scrolling inside
 * the panel moves through time. Most recent week sits nearest the camera.
 */
const COLS = 7;
const CFG = {
  barGap: 1.5, barWidth: 0.75, maxHeight: 2, minHeight: 0.06, globeR: 94,
  camFov: 34, eye: [15.5, 21.5, -3.5], center: [5, 8, 4],
  visibleWeeks: 20, weekOffset: 5, scrollMaxDelta: 30, logDivider: 2,
};
// teal ramps (RGBA 0..1) — tuned to the site's palette
const LIGHT = [[0.86, 0.85, 0.82, 1], [0.60, 0.93, 0.87, 1], [0.33, 0.85, 0.77, 1], [0.08, 0.70, 0.64, 1], [0.06, 0.44, 0.41, 1]];
const DARK = [[0.17, 0.18, 0.20, 1], [0.05, 0.33, 0.30, 1], [0.08, 0.50, 0.45, 1], [0.16, 0.72, 0.65, 1], [0.32, 0.90, 0.80, 1]];
const FOG_LIGHT = [0.953, 0.945, 0.925];
const FOG_DARK = [0.082, 0.086, 0.098];

const VS = `
attribute vec3 aPos; attribute vec4 aColor; attribute float aHeight; attribute float aIdx;
uniform mat4 uMVP; uniform float uScroll; uniform float uR; uniform float uHover;
varying vec4 vColor; varying float vShade; varying float vFog; varying float vHi;
void main(){
  vHi = (abs(aIdx - uHover) < 0.5) ? 1.0 : 0.0;
  float wx = aPos.x + uScroll;
  float ang = wx / uR;
  float sy = uR * cos(ang) - uR;
  float sz = uR * sin(ang);
  float ny = cos(ang), nz = sin(ang);
  vec3 p = vec3(aPos.z, sy + ny * aPos.y, sz + nz * aPos.y);
  gl_Position = uMVP * vec4(p, 1.0);
  float top = smoothstep(0.0, aHeight * 0.6, aPos.y);
  vShade = 0.55 + 0.45 * top;
  vFog = smoothstep(80.0, 160.0, length(gl_Position.xyz));
  vColor = aColor;
}`;
const FS = `
precision mediump float;
varying vec4 vColor; varying float vShade; varying float vFog; varying float vHi;
uniform vec4 uFog;
void main(){
  vec4 c = vColor * vShade;
  if (vHi > 0.5) c.rgb = mix(c.rgb, vec3(1.0), 0.3);
  c = mix(c, uFog, vFog); c.a = 1.0;
  gl_FragColor = c;
}`;

function perspective(fov, aspect, near, far) {
  const f = 1 / Math.tan((fov * Math.PI) / 360), nf = 1 / (near - far);
  return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0]);
}
function lookAt(e, c, up) {
  let zx = e[0] - c[0], zy = e[1] - c[1], zz = e[2] - c[2];
  let l = 1 / Math.hypot(zx, zy, zz); zx *= l; zy *= l; zz *= l;
  let xx = up[1] * zz - up[2] * zy, xy = up[2] * zx - up[0] * zz, xz = up[0] * zy - up[1] * zx;
  l = Math.hypot(xx, xy, xz); l = l ? 1 / l : 0; xx *= l; xy *= l; xz *= l;
  const yx = zy * xz - zz * xy, yy = zz * xx - zx * xz, yz = zx * xy - zy * xx;
  return new Float32Array([xx, yx, zx, 0, xy, yy, zy, 0, xz, yz, zz, 0,
    -(xx * e[0] + xy * e[1] + xz * e[2]), -(yx * e[0] + yy * e[1] + yz * e[2]), -(zx * e[0] + zy * e[1] + zz * e[2]), 1]);
}
function mul(a, b) {
  const o = new Float32Array(16);
  for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++)
    o[j * 4 + i] = a[i] * b[j * 4] + a[4 + i] * b[j * 4 + 1] + a[8 + i] * b[j * 4 + 2] + a[12 + i] * b[j * 4 + 3];
  return o;
}
function project(x, y, z, m, w, h) {
  const cx = m[0] * x + m[4] * y + m[8] * z + m[12], cy = m[1] * x + m[5] * y + m[9] * z + m[13], cw = m[3] * x + m[7] * y + m[11] * z + m[15];
  if (cw <= 0.001) return null;
  return [((cx / cw) * 0.5 + 0.5) * w, (1 - ((cy / cw) * 0.5 + 0.5)) * h];
}
const levelFor = (c) => (c === 0 ? 0 : c <= 5 ? 1 : c <= 10 ? 2 : c <= 15 ? 3 : 4);
const isDark = () => document.documentElement.classList.contains('dark');
const fmtMonth = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
const fmtFull = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

function pushBox(V, C, H, I, x, z, h, col, idx) {
  const w = CFG.barWidth / 2;
  const f = [
    [-w, 0, w], [w, 0, w], [w, h, w], [-w, 0, w], [w, h, w], [-w, h, w],
    [w, 0, -w], [-w, 0, -w], [-w, h, -w], [w, 0, -w], [-w, h, -w], [w, h, -w],
    [-w, 0, -w], [-w, 0, w], [-w, h, w], [-w, 0, -w], [-w, h, w], [-w, h, -w],
    [w, 0, w], [w, 0, -w], [w, h, -w], [w, 0, w], [w, h, -w], [w, h, w],
    [-w, h, w], [w, h, w], [w, h, -w], [-w, h, w], [w, h, -w], [-w, h, -w],
    [-w, 0, -w], [w, 0, -w], [w, 0, w], [-w, 0, -w], [w, 0, w], [-w, 0, w],
  ];
  for (const [fx, fy, fz] of f) { V.push(x + fx, fy, z + fz); C.push(col[0], col[1], col[2], col[3]); H.push(h); I.push(idx); }
}

export default function ContributionLandscape({ days, onDayClick }) {
  const ctl = useRef({ replay: () => {} });
  const [replaying, setReplaying] = useState(false);
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const [label, setLabel] = useState('');
  const [tip, setTip] = useState(null);
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current, viewport = viewportRef.current;
    if (!canvas || !days?.length) return;
    const gl = canvas.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: false });
    if (!gl) { setUnsupported(true); return; }

    // pad so row 0 = Sunday
    const first = new Date(days[0].date + 'T00:00:00');
    const pad = [];
    for (let p = first.getDay() - 1; p >= 0; p--) { const d = new Date(first); d.setDate(d.getDate() - (p + 1)); pad.push({ date: d.toISOString().slice(0, 10), count: 0, _pad: true }); }
    const all = pad.concat(days);
    const weeks = Math.ceil(all.length / COLS);

    const bars = [];
    all.forEach((c, i) => {
      if (c._pad) return;
      const norm = c.count === 0 ? 0 : Math.log(c.count + 1) / CFG.logDivider;
      const h = c.count === 0 ? CFG.minHeight : CFG.minHeight + norm * (CFG.maxHeight - CFG.minHeight);
      bars.push({ weekIdx: Math.floor(i / COLS), row: i % COLS, h, level: levelFor(c.count), date: c.date, count: c.count });
    });

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const maxScroll = Math.max(0, weeks * CFG.barGap - ((isMobile ? CFG.visibleWeeks / 2 : CFG.visibleWeeks) / 3) * CFG.barGap);

    // geometry
    const V = [], C = [], H = [], I = [];
    const build = () => {
      V.length = C.length = H.length = I.length = 0;
      const pal = isDark() ? DARK : LIGHT;
      bars.forEach((b, i) => pushBox(V, C, H, I, (weeks - 1 - b.weekIdx) * CFG.barGap, (b.row - COLS / 2) * CFG.barGap, b.h, pal[b.level], i));
    };
    build();

    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
    const prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog); gl.useProgram(prog);
    const A = { pos: gl.getAttribLocation(prog, 'aPos'), col: gl.getAttribLocation(prog, 'aColor'), h: gl.getAttribLocation(prog, 'aHeight'), idx: gl.getAttribLocation(prog, 'aIdx') };
    const U = { mvp: gl.getUniformLocation(prog, 'uMVP'), scroll: gl.getUniformLocation(prog, 'uScroll'), r: gl.getUniformLocation(prog, 'uR'), fog: gl.getUniformLocation(prog, 'uFog'), hover: gl.getUniformLocation(prog, 'uHover') };
    const buf = { pos: gl.createBuffer(), col: gl.createBuffer(), h: gl.createBuffer(), idx: gl.createBuffer() };
    const upload = () => {
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.pos); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(V), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.col); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(C), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.h); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(H), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.idx); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(I), gl.STATIC_DRAW);
    };
    upload();
    gl.enable(gl.DEPTH_TEST);

    // theme changes → rebuild colors
    const mo = new MutationObserver(() => { build(); upload(); });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    // scroll state
    let scroll = 0, target = 0, mvp = null, hover = -1, px = -1, py = -1, replay = false, downAt = null;
    ctl.current.replay = () => { target = scroll = -maxScroll; replay = true; setReplaying(true); };
    const onWheel = (e) => {
      const raw = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY * 0.5 : e.deltaX * 0.5;
      const d = Math.max(-CFG.scrollMaxDelta, Math.min(CFG.scrollMaxDelta, raw));
      if (replay) { replay = false; setReplaying(false); }
      target = Math.max(-maxScroll, Math.min(0, target + d * 0.15));
      e.preventDefault();
    };
    let tx = 0, ty = 0, ts = 0, axis = null;
    const onTS = (e) => { tx = e.touches[0].clientX; ty = e.touches[0].clientY; ts = target; axis = null; };
    const onTM = (e) => {
      const dx = e.touches[0].clientX - tx, dy = e.touches[0].clientY - ty;
      if (!axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (!axis) return;
      target = Math.max(-maxScroll, Math.min(0, ts - (axis === 'x' ? dx : -dy) * 0.4));
      e.preventDefault();
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('touchstart', onTS, { passive: true });
    viewport.addEventListener('touchmove', onTM, { passive: false });

    let hoveredBar = null;
    const updateHover = (mx, my) => {
      if (!mvp) return;
      const rect = canvas.getBoundingClientRect();
      let best = null, bestIdx = -1, bestD = Infinity;
      for (let i = 0; i < bars.length; i++) {
        const b = bars[i];
        const bx = (weeks - 1 - b.weekIdx) * CFG.barGap + scroll;
        const ang = bx / CFG.globeR, ca = Math.cos(ang), sa = Math.sin(ang);
        const sy = CFG.globeR * ca - CFG.globeR, sz = CFG.globeR * sa, z3 = (b.row - COLS / 2) * CFG.barGap;
        const pb = project(z3, sy, sz, mvp, rect.width, rect.height), pt = project(z3, sy + ca * b.h, sz + sa * b.h, mvp, rect.width, rect.height);
        if (!pb || !pt) continue;
        const minY = Math.min(pb[1], pt[1]) - 4, maxY = Math.max(pb[1], pt[1]) + 4, ax = (pb[0] + pt[0]) / 2;
        if (mx >= ax - 12 && mx <= ax + 12 && my >= minY && my <= maxY) {
          const d = Math.hypot(ax - mx, (minY + maxY) / 2 - my);
          if (d < bestD) { bestD = d; best = b; bestIdx = i; }
        }
      }
      hover = bestIdx; hoveredBar = best;
      setTip(best ? { x: Math.min(mx + 14, rect.width - 190), y: my - 48, count: best.count, date: fmtFull(best.date) } : null);
    };
    const onMove = (e) => { const r = canvas.getBoundingClientRect(); px = e.clientX - r.left; py = e.clientY - r.top; updateHover(px, py); };
    const onLeave = () => { hover = -1; px = py = -1; setTip(null); };
    const onDown = (e) => { downAt = [e.clientX, e.clientY]; };
    const onUp = (e) => { if (downAt && Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]) < 5 && hoveredBar && onDayClick) onDayClick(hoveredBar.date, hoveredBar.count); downAt = null; };
    canvas.addEventListener('mousedown', onDown); canvas.addEventListener('mouseup', onUp);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    let raf, lastLabel = '';
    const frame = () => {
      if (replay) { target = Math.min(0, target + maxScroll / 420); if (target >= 0) { replay = false; setReplaying(false); } }
      const diff = target - scroll;
      scroll += diff * 0.1;
      if (Math.abs(diff) < 0.01) scroll = target;
      if (px >= 0 && Math.abs(diff) > 0.02) updateHover(px, py);

      const rw = Math.round(-scroll / CFG.barGap);
      const ow = Math.max(0, Math.min(weeks - CFG.weekOffset, weeks - CFG.weekOffset - rw));
      const di = Math.min(ow * COLS, all.length - 1);
      const lb = all[di] ? fmtMonth(all[di].date) : '';
      if (lb !== lastLabel) { lastLabel = lb; setLabel(lb); }

      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.round(rect.width * dpr), h = Math.round(rect.height * dpr);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }

      const dark = isDark(), fog = dark ? FOG_DARK : FOG_LIGHT;
      gl.clearColor(fog[0], fog[1], fog[2], 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      const shift = rect.width <= 640 ? -4 : 0;
      const eye = [CFG.eye[0] + shift, CFG.eye[1], CFG.eye[2] + shift], ctr = [CFG.center[0] + shift, CFG.center[1], CFG.center[2] + shift];
      mvp = mul(perspective(CFG.camFov, w / h, 0.5, 300), lookAt(eye, ctr, [0, 1, 0]));

      gl.uniform4f(U.fog, fog[0], fog[1], fog[2], 1);
      gl.uniformMatrix4fv(U.mvp, false, mvp);
      gl.uniform1f(U.scroll, scroll); gl.uniform1f(U.r, CFG.globeR); gl.uniform1f(U.hover, hover);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.pos); gl.enableVertexAttribArray(A.pos); gl.vertexAttribPointer(A.pos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.col); gl.enableVertexAttribArray(A.col); gl.vertexAttribPointer(A.col, 4, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.h); gl.enableVertexAttribArray(A.h); gl.vertexAttribPointer(A.h, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.idx); gl.enableVertexAttribArray(A.idx); gl.vertexAttribPointer(A.idx, 1, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLES, 0, V.length / 3);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf); mo.disconnect();
      viewport.removeEventListener('wheel', onWheel); viewport.removeEventListener('touchstart', onTS); viewport.removeEventListener('touchmove', onTM);
      canvas.removeEventListener('mousemove', onMove); canvas.removeEventListener('mouseleave', onLeave); canvas.removeEventListener('mousedown', onDown); canvas.removeEventListener('mouseup', onUp);
      Object.values(buf).forEach((b) => gl.deleteBuffer(b)); gl.deleteProgram(prog);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (unsupported) return <p className="github-empty" style={{ padding: 40, textAlign: 'center' }}>WebGL is required for the 3D contribution graph.</p>;

  return (
    <div className="land">
      <div className="land__viewport" ref={viewportRef}>
        <canvas ref={canvasRef} className="land__canvas" />
        {label && <div className="land__date">{label}</div>}
        <button className={`land__replay${replaying ? ' on' : ''}`} onClick={() => ctl.current.replay()} title="Replay the year">{replaying ? '▶ Replaying…' : '↻ Replay the year'}</button>
        {tip && <div className="land__tip" style={{ left: tip.x, top: tip.y }}><strong>{tip.count} contribution{tip.count === 1 ? '' : 's'}</strong><span>{tip.date}</span></div>}
      </div>
      <p className="land__hint">Scroll inside the graph to travel back in time · click a bar to see what shipped that day</p>
    </div>
  );
}
